import { useEffect, useState, useRef } from "react";
import { createPopper } from "@popperjs/core";
import { ChevronDown, Check, Search, X } from "lucide-react";

export default function FlexibleLanguageSelect({
  dropdownData = [],
  selectedValue,
  onChange = () => {},
  placeholder = "Select languages",
  isSearch = false,
  placement = "bottom-start",
  dropdownClass = "",
  dropdownButtonClass = "",
  optionsClass = "",
  onSearchHandler = () => {},
  onScroll = () => {},
  loading = false,
  label,
  labelClasses = "",
  errorType,
  errorText,
  required = false,
  isDisabled = false,
  checked = true,
  buttonStyle = "",
  searchValue = "",
  type = "single", // "single" | "multiple"
  displayKey = "name",
  valueKey = "value",
  imageKey = "image",
  isDropdownOpen = false,
  codeKey = "code",
  defaultSelected = null,
  onDefaultChange = () => {},
  ...props
}) {
  const [selected, setSelected] = useState(type === "multiple" ? [] : null);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(isDropdownOpen || false);
  const containerRef = useRef(null);
  const referenceRef = useRef(null);
  const popperRef = useRef(null);
  const popperInstanceRef = useRef(null);
  const [defaultOption, setDefaultOption] = useState(null);

  useEffect(() => {
    if (referenceRef.current && popperRef.current && open) {
      popperInstanceRef.current = createPopper(referenceRef.current, popperRef.current, {
        placement,
        modifiers: [
          { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
          { name: "offset", options: { offset: [0, 8] } },
        ],
      });
    }

    return () => {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy();
        popperInstanceRef.current = null;
      }
    };
  }, [open, placement]);

  useEffect(() => {
    setList(dropdownData);
  }, [JSON.stringify(dropdownData)]);

  useEffect(() => {
    if ((selectedValue || selectedValue !== undefined) && Array.isArray(dropdownData) && dropdownData.length > 0) {
      if (type === "multiple") {
        const matched = dropdownData.filter(
          (item) => selectedValue.includes(item[valueKey]) || selectedValue.includes(item._id)
        );
        setSelected(matched);

        const foundDefault = matched.find((item) => item[valueKey] === defaultSelected || item._id === defaultSelected);
        setDefaultOption(foundDefault || null);
      } else {
        const match = dropdownData.find((item) => item[valueKey] === selectedValue || item._id === selectedValue);
        setSelected(match || null);
      }
    } else {
      setSelected(type === "multiple" ? [] : null);
      setDefaultOption(null);
    }
  }, [JSON.stringify(selectedValue), JSON.stringify(defaultSelected), JSON.stringify(dropdownData)]);

  const handleClickOutside = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    if (type === "multiple") {
      const already = selected.some((sel) => sel[valueKey] === item[valueKey]);
      let updated = already ? selected.filter((sel) => sel[valueKey] !== item[valueKey]) : [...selected, item];

      if (already && defaultOption && defaultOption[valueKey] === item[valueKey]) {
        setDefaultOption(null);
        onDefaultChange(null);
      }

      setSelected(updated);
      onChange(updated.map((v) => v[valueKey]));
    } else {
      setSelected(item);
      setOpen(false);
      onChange(item[valueKey]);
    }
  };

  const handleSetDefault = (item) => {
    if (!selected.some((sel) => sel[valueKey] === item[valueKey])) return;
    setDefaultOption(item);
    onDefaultChange(item[valueKey]);
  };

  const clearSelection = () => {
    setSelected(type === "multiple" ? [] : null);
    onChange(type === "multiple" ? [] : null);
  };

  const displayText = () => {
    if (type === "multiple") {
      if (!Array.isArray(selected) || selected.length === 0) return placeholder;
      return `${selected.length} selected`;
    }
    if (!selected) return placeholder;
    return selected[displayKey];
  };

  const filteredList = list.filter((option) => option?.[displayKey]?.toLowerCase().includes(search.toLowerCase()));

  const handleSelectAll = () => {
    if (type === "multiple") {
      const all = list.filter((option) => option?.[displayKey]?.toLowerCase().includes(search.toLowerCase()));
      setSelected(all);
      onChange(all.map((v) => v[valueKey]));
    }
  };

  return (
    <div ref={containerRef} className={`relative w-full ${props.className || ""}`}>
      {label && (
        <label className={`text-xs font-medium mb-1 ${labelClasses}`}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div ref={referenceRef}>
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => setOpen(!open)}
          className={`w-full h-10 flex items-center justify-between rounded-md bg-white border px-3 text-sm text-gray-800 ${
            isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          } ${buttonStyle} ${dropdownButtonClass}`}>
          <span className="truncate">{displayText()}</span>
          <ChevronDown
            className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open && (
        <div
          ref={popperRef}
          className="z-20 bg-white border w-full rounded-md shadow-md min-w-[200px] max-h-60 overflow-auto"
          onScroll={onScroll}>
          {isSearch && (
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    onSearchHandler(e.target.value);
                  }}
                  placeholder="Search..."
                  className="w-full pl-8 pr-2 py-2 text-sm border rounded-md"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      onSearchHandler("");
                    }}
                    className="absolute right-2 top-2.5 text-gray-400 hover:text-black">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}
          {filteredList.length === 0 ? (
            <div className="text-center p-3 text-sm text-gray-500">No options found</div>
          ) : (
            filteredList.map((item) => {
              const isChecked = selected.some((sel) => sel[valueKey] === item[valueKey]);
              const isDefault = defaultOption?.[valueKey] === item[valueKey];

              return (
                <div
                  key={item[valueKey]}
                  className="flex items-center justify-between px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelect(item)}>
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-600 mr-2 rounded-sm"
                    checked={isChecked}
                    onChange={() => handleSelect(item)}
                    onClick={(e) => e.stopPropagation()}
                  />

                  <div className="flex-grow flex flex-col">
                    <span className="text-sm font-semibold text-gray-900">{item[displayKey]}</span>
                    <span className="text-xs text-gray-500">{item[codeKey]}</span>
                  </div>

                  <input
                    type="radio"
                    name="defaultLanguage"
                    className="form-radio text-blue-500"
                    checked={isDefault}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetDefault(item);
                    }}
                    disabled={!isChecked}
                    readOnly
                  />
                </div>
              );
            })
          )}
        </div>
      )}

      {errorType && errorText && (
        <div
          className={`text-xs mt-1 ${
            errorType === "error"
              ? "text-red-600"
              : errorType === "warning"
              ? "text-amber-600"
              : errorType === "success"
              ? "text-green-600"
              : "text-sky-600"
          }`}>
          <i
            className={`fa-regular fa-fw text-sm mr-1 ${
              errorType === "error"
                ? "fa-square-exclamation"
                : errorType === "warning"
                ? "fa-triangle-exclamation"
                : errorType === "success"
                ? "fa-circle-check"
                : "fa-circle-info"
            }`}
          />
          {errorText}
        </div>
      )}
    </div>
  );
}
